// Top-level Preact app shell. Per spec/tech-arch.md §3.1 + §4 + §6
// and the P-022 / P-023 / P-024 build-plan rows.
//
// **Layout.** Three rows: header bar (clickable chapter logo) →
// persistence banner (renders only when `usePersistenceError()` is
// non-null, per D-083) → main content area. The banner mounts below
// the header so the chrome remains stable while the alert stays highly
// visible (decision documented at P-022 close, D-084).
//
// **Header bar (P-037.25 D-133; P-037.30 D-138 §item 3).** Chapter
// logo image (`/ypo-s7-white.png`, 250 × 42 native — same dimensions
// as the footer logo `/ypo-s7.png`, just inverted-color for the navy
// nav bar) wrapped in an `<a href="#/">` so clicking it returns to
// Home. The prior text-logo + Help-link affordance (P-022 placeholder
// per build-plan P-022 row decision surface) was dropped at D-133 —
// the help screen never shipped and the link was a no-op anchor.
//
// D-138 §item 3 restored an app-name text label adjacent to the logo:
// `<img alt="YPO Southern 7"> | <span>Email Builder</span>`. Mirrors
// the "YPO | Southern 7 Chapter" pattern Banks called out from the
// Header Block — image is the chapter wordmark; adjacent text spells
// out the app name. The image's `alt` shrinks to "YPO Southern 7"
// (was "S7 Email Builder") so screen readers don't double-read the
// app name. The text span carries `aria-label` semantics through the
// h1 wrapper — no `aria-hidden` needed.
//
// **Route-driven main content (P-023).** `<main>` mounts `<RouterOutlet
// />` (src/app/routes.tsx), which reads `useRoute()` and renders the
// matched screen for the current `window.location.hash`. The default
// route (`#/`) renders `<HomeScreen />`; unknown hashes redirect to
// `#/` via `<RouterOutlet />`'s effect. The previous P-022 single-
// screen Home placeholder moved into src/app/screens/home.tsx; the
// `<App />` shell stays a thin layout chrome + slot.
//
// **`<PageContainer />` for the centered content well (P-024).** Both
// the header inner-div and the main inner-div use the same `mx-auto
// max-w-screen-lg px-4` pattern; deferred at P-022 / P-023 below the
// 3-use threshold. P-024's screens add the third-plus consumers, so
// the pattern hoists to `src/app/page-container.tsx` and is consumed
// here + by every list screen.
//
// **Route-aware main-content width (P-037.26 D-134).** The editor
// screens (Email editor + Template editor) host a two-pane layout
// where the preview pane needs ≥680 px to render the iframe at
// `tokens.layout.containerWidth`. At the default 1024 px page width
// + 50/50 grid each pane gets ~488 px and the iframe gets clipped.
// App.tsx reads the current route and passes `size="wide"` (max-w-
// screen-xl, 1280 px) to PageContainer for those routes; everything
// else stays at the default `'normal'` (max-w-screen-lg, 1024 px).
// The chapter-staff editor is desktop-first per the P-037.26
// handoff item 7a rationale.
//
// **Sign-out button (P-045 / D-140).** Right-aligned text link in the
// top-right of the nav. Banks picked the simplest treatment at the
// P-045 session opening (Q3) — plain text link, no email display, no
// icon. Click dynamically imports `auth.signOut()` so bcryptjs +
// Firestore stay out of app.tsx's static import graph (Tech Arch
// v2-auth §8). P-046 added a Settings link adjacent to this button.
//
// **Settings link (P-046 / D-141).** Anchor sibling to Sign-out,
// rendered first so the visual order reads `[Settings] [Sign out]`
// while Sign-out keeps its canonical-right slot via `ml-auto` on the
// outer wrapping element. Matches Sign-out's white-text + hover-
// underline styling for visual parity.
//
// **Tailwind utilities.** Token-aware via the `tailwind.config.ts`
// bindings — the chapter-brand `bg-ypo-navy` resolves to
// `tokens.color['ypo-navy']` (D-019 / D-027). Stock Tailwind defaults
// (`text-white`, `text-gray-*`, etc.) are intentionally available via
// `theme.extend` and used here for non-brand neutrals.

import { useRoute } from './hooks/use-route'
import { PageContainer } from './page-container'
import { PersistenceBanner } from './persistence-banner'
import { type RouteName, RouterOutlet } from './routes'
import { SkipLink } from './skip-link'
import { ToastContainer } from './toast/toast-container'

async function handleSignOut() {
  const { signOut } = await import('../lib/auth')
  signOut()
}

const WIDE_ROUTES: ReadonlySet<RouteName> = new Set(['email-editor', 'template-editor'])

export function App() {
  const route = useRoute()
  const size = WIDE_ROUTES.has(route.name) ? 'wide' : 'normal'
  return (
    <div class="flex min-h-screen flex-col bg-white font-sans text-gray-900">
      <SkipLink />
      <header class="bg-ypo-navy text-white">
        <PageContainer y="tight" class="flex items-center gap-4">
          <h1 class="m-0 flex items-center gap-3">
            <a href="#/" class="inline-block">
              <img
                src="/ypo-s7-white.png"
                alt="YPO Southern 7"
                width={250}
                height={42}
                class="block"
              />
            </a>
            <span aria-hidden="true" class="select-none text-2xl font-light text-gray-300">
              |
            </span>
            <span class="text-base font-medium text-white">Email Builder</span>
          </h1>
          <a
            href="#/settings"
            class="ml-auto text-sm text-white underline-offset-2 hover:underline focus:underline focus:outline-none"
          >
            Settings
          </a>
          <button
            type="button"
            onClick={handleSignOut}
            class="text-sm text-white underline-offset-2 hover:underline focus:underline focus:outline-none"
          >
            Sign out
          </button>
        </PageContainer>
      </header>
      <PersistenceBanner />
      <main id="main-content" tabIndex={-1} class="flex-1 focus:outline-none">
        <PageContainer size={size}>
          <RouterOutlet />
        </PageContainer>
      </main>
      <ToastContainer />
    </div>
  )
}
